/**
 * SaveAccountRateCard — plan header ▸ "Save as Account Rate Card".
 *
 * When a plan's Account has no negotiated rates yet, the custom rates the plan was priced
 * at are the natural first offer. This saves each Labor Category the plan prices AWAY
 * FROM the standard Rate Card as one Account Rate Override (RP-12.4) — so the next plan
 * on the Account picks them up without anyone re-keying them on the Account page.
 * Categories at the standard rate are not saved; they keep following the standard card.
 *
 * It only SEEDS a Rate Card. The button hides once the Account has negotiated rates on
 * the plan's basis, and the server refuses a save that would collide; changing rates
 * after that happens on the Account's own Rate Card, row by row, with its own reasons.
 *
 * Visibility and permission come straight off the API response (`canManage`) — this
 * component never re-derives permission from role. Bill rates only; no cost is shown.
 *
 * TERMINOLOGY (§3): "Labor Category" never "Role"; "Rate Basis" never "price list";
 * "Account" never "Client".
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type PlanAccountRateCardProposalView,
  usePlanAccountRateCard,
  useSaveAccountRateCardFromPlan,
} from "@/hooks/use-account-rate-overrides";
import { parseMinor } from "@/lib/rate-card";

const schema = z.object({
  scope: z.enum(["planBasis", "allBases"]),
  effectiveStart: z.string().min(1, "Effective start is required"),
  reason: z.string().trim().min(1, "Reason is required").max(500),
  sourceDocumentReference: z.string().trim().max(200),
  /** laborCategoryId → chosen rate, only for Labor Categories priced more than one way. */
  choices: z.record(z.string(), z.string()),
});

type FormValues = z.infer<typeof schema>;

/** A `<input type="date">` value ("YYYY-MM-DD") to a UTC-midnight ISO 8601 timestamp. */
function toIsoMidnight(dateOnly: string): string {
  return new Date(`${dateOnly}T00:00:00.000Z`).toISOString();
}

function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatRate(value: string | null): string {
  return value === null ? "—" : `$${value}`;
}

/** Same comparison the server makes: whole cents, never float arithmetic on the strings. */
function sameRate(a: string, b: string): boolean {
  return parseMinor(a, 2) === parseMinor(b, 2);
}

/**
 * The header button. Renders nothing unless this reader may set the Account's Rate Card,
 * the Account has no negotiated rates on the plan's basis, and the plan has at least one
 * rate that is not the standard rate.
 */
/**
 * Whether the plan header's ⋯ menu offers "Save as Account Rate Card", and the hover text
 * for it. Offered only while the Account has no negotiated rates on the plan's basis and
 * the plan actually prices away from the standard card.
 */
export function useSaveAccountRateCardOffer(planId: string): { offered: boolean; title: string } {
  const proposal = usePlanAccountRateCard(planId);
  const data = proposal.data;
  if (
    !data?.canManage ||
    data.existingOnPlanBasisCount > 0 ||
    data.categories.every((c) => c.atStandard)
  ) {
    return { offered: false, title: "" };
  }
  return {
    offered: true,
    title: `${data.accountName} has no negotiated rates yet. Save this plan's custom rates as its Rate Card.`,
  };
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string;
}

export function SaveAccountRateCardDialog({ open, onOpenChange, planId }: DialogProps) {
  const proposal = usePlanAccountRateCard(planId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Save as Account Rate Card</DialogTitle>
          <DialogDescription>
            Saves this plan's custom rates — the ones that differ from the standard Rate Card — as
            the Account's negotiated rates. Labor Categories at the standard rate are not saved and
            keep following the standard card. New plans on the Account pick them up automatically;
            existing plans keep their rates.
          </DialogDescription>
        </DialogHeader>
        {proposal.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : proposal.isError ? (
          <ErrorState
            message={`Could not load this plan's rates: ${proposal.error.message}`}
            onRetry={() => proposal.refetch()}
          />
        ) : proposal.data ? (
          <SaveAccountRateCardForm
            open={open}
            proposal={proposal.data}
            onDone={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SaveAccountRateCardForm({
  open,
  proposal,
  onDone,
}: {
  open: boolean;
  proposal: PlanAccountRateCardProposalView;
  onDone: () => void;
}) {
  const save = useSaveAccountRateCardFromPlan(proposal.planId, proposal.accountId);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      scope: "planBasis",
      effectiveStart: todayDateOnly(),
      reason: `Rates from plan "${proposal.planName}".`,
      sourceDocumentReference: "",
      choices: {},
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      scope: "planBasis",
      effectiveStart: todayDateOnly(),
      reason: `Rates from plan "${proposal.planName}".`,
      sourceDocumentReference: "",
      choices: {},
    });
  }, [open, proposal.planName, form]);

  const scope = form.watch("scope");
  const choices = form.watch("choices");
  const allBasesBlocked = scope === "allBases" && proposal.existingAnyBasisCount > 0;
  const conflicted = proposal.categories.filter((c) => c.proposedRate === null);
  const unresolved = conflicted.filter((c) => !choices[c.laborCategoryId]);
  // What will actually be saved, given the choices so far: every custom rate, and every
  // chosen rate that is not the standard one.
  const savingCount = proposal.categories.filter((c) => {
    if (c.proposedRate !== null) return !c.atStandard;
    const choice = choices[c.laborCategoryId];
    return choice !== undefined && !c.standardPlanRates.some((r) => sameRate(choice, r));
  }).length;

  async function onSubmit(values: FormValues) {
    try {
      const result = await save.mutateAsync({
        scope: values.scope,
        effectiveStart: toIsoMidnight(values.effectiveStart),
        reason: values.reason,
        sourceDocumentReference: values.sourceDocumentReference.trim() || null,
        choices: conflicted.length > 0 ? values.choices : undefined,
      });
      const onStandard =
        result.standardCount > 0 ? ` ${result.standardCount} stay on the standard card.` : "";
      toast.success(
        `Saved ${result.createdCount} negotiated rate${result.createdCount === 1 ? "" : "s"} for ${proposal.accountName}.${onStandard}`,
      );
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save the Account Rate Card.");
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Labor Category</th>
                <th
                  className="px-3 py-2 text-right font-medium"
                  title={`The standard Rate Card on ${proposal.rateBasisLabel}`}
                >
                  Standard rate
                </th>
                <th className="px-3 py-2 text-right font-medium">Rate to save</th>
              </tr>
            </thead>
            <tbody>
              {proposal.categories.map((c) => (
                <tr key={c.laborCategoryId} className="border-t border-border">
                  <td className="px-3 py-2">{c.laborCategoryName}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatRate(c.cardRate)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {c.atStandard ? (
                      <span className="font-sans text-muted-foreground">Standard · not saved</span>
                    ) : c.proposedRate !== null ? (
                      formatRate(c.proposedRate)
                    ) : (
                      <FormField
                        control={form.control}
                        name={`choices.${c.laborCategoryId}`}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value ?? ""}>
                            <SelectTrigger
                              className="ml-auto w-40 font-sans"
                              aria-label={`Rate to save for ${c.laborCategoryName}`}
                            >
                              <SelectValue placeholder="Choose a rate" />
                            </SelectTrigger>
                            <SelectContent>
                              {c.planRates.map((rate) => (
                                <SelectItem key={rate} value={rate}>
                                  {formatRate(rate)}
                                  {c.standardPlanRates.some((r) => sameRate(rate, r))
                                    ? " · standard, not saved"
                                    : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {conflicted.length > 0 && (
          <p className="text-xs text-muted-foreground">
            This plan uses more than one rate for{" "}
            {conflicted.map((c) => c.laborCategoryName).join(", ")}. Choose the one to save.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Applies to</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="planBasis">{proposal.rateBasisLabel} only</SelectItem>
                    <SelectItem value="allBases">Every basis</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  {allBasesBlocked
                    ? `${proposal.accountName} already has negotiated rates on another basis, so these can only apply to ${proposal.rateBasisLabel}.`
                    : "The Rate Basis this plan was priced on, unless the Account was quoted one rate for every basis."}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="effectiveStart"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Effective from</FormLabel>
                <FormControl>
                  <Input type="date" min={todayDateOnly()} {...field} />
                </FormControl>
                <FormDescription>
                  Today or later — open-ended until someone changes them.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="sourceDocumentReference"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Source document reference</FormLabel>
              <FormControl>
                <Input placeholder="Optional — SOW or order form reference" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              save.isPending || allBasesBlocked || unresolved.length > 0 || savingCount === 0
            }
          >
            {save.isPending
              ? "Saving…"
              : `Save ${savingCount} custom rate${savingCount === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
